import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { getNoty } from 'Api/endpoints/User';
import useInterval from 'Util/useInterval';
import { useSelector } from 'react-redux';
import { useError } from 'Api/hooks/useError';

const useNotification = () => {
  const { notificationCollapsed } = useSelector(state => state.settings);
  const [notifications, setNotifications] = useState([]);
  const loadingRef = useRef(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
  });
  const [ref, inView] = useInView({
    threshold: 0.5,
  });
  const { showErrorPopup } = useError();

  const fetchNotifications = useCallback(async pageNum => {
    try {
      if (loadingRef.current) return;
      loadingRef.current = true;
      const result = await getNoty({ page: pageNum });
      if (result.err) {
        showErrorPopup({ text: result.err.response.data.message });
        return;
      }
      if (pageNum === 1) {
        setNotifications(result.data.items);
      } else {
        setNotifications(prev => [...prev, ...result.data.items]);
      }
      setPagination({
        currentPage: pageNum,
        lastPage: result.data.lastPage,
      });
    } catch (e) {
      console.log(e);
    } finally {
      loadingRef.current = false;
    }
  }, []);

  const refreshAll = useCallback(() => {
    setPagination({
      currentPage: 1,
      lastPage: 1,
    });
    fetchNotifications(1);
  }, [fetchNotifications]);

  useInterval(() => {
    if (!notificationCollapsed) {
      refreshAll();
    }
  }, 30000);

  useEffect(() => {
    if (!notificationCollapsed) {
      fetchNotifications(1);
    }
  }, [fetchNotifications, notificationCollapsed]);

  useEffect(() => {
    if (inView && !loadingRef.current && pagination.currentPage < pagination.lastPage) {
      fetchNotifications(pagination.currentPage + 1);
    }
  }, [inView, pagination.currentPage, pagination.lastPage, fetchNotifications]);

  const isLastPage = useMemo(() => pagination.currentPage >= pagination.lastPage, [pagination]);

  return { notifications, infiniteScrollRef: ref, isLastPage };
};

export default useNotification;
